import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ToggleButton from "@mui/material/ToggleButton";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import Slider from "@mui/material/Slider";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import { setShowVehicles, setVizParam, setNetworkOpacity } from "../../reducers/RealTimeParamSlice";
import { PROGRESS_BAR_HEIGHT } from "./RealTimeProgress";

const PANEL_WIDTH = 380;
// Leaves the map's fullscreen / zoom controls at the top right uncovered
const PANEL_TOP_OFFSET = 72;

// vizParam values understood by RealTimeMap, paired with their label in
// lang.realTimePanelTitles.vizParamRadio.options (which keys the operator by `operatorref`).
const VIZ_PARAMS = [
  { value: "velocity", labelKey: "velocity" },
  { value: "bearing", labelKey: "bearing" },
  { value: "operator", labelKey: "operatorref" },
];

// A labelled block of controls, optionally with a readout opposite the heading.
// Sections are spaced evenly by the parent Stack, so they only carry their heading.
const Section = ({ label, labelId, readout, children }) => (
  <Box component="section">
    <Box
      sx={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 1,
        mb: 1,
      }}
    >
      <Typography
        id={labelId}
        variant="overline"
        sx={{ color: "text.secondary", letterSpacing: "0.09em", lineHeight: 1.6 }}
      >
        {label}
      </Typography>
      {readout && (
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontVariantNumeric: "tabular-nums" }}
        >
          {readout}
        </Typography>
      )}
    </Box>
    {children}
  </Box>
);

// Collapsible panel overlaid on the right hand side of the realtime map.
// Filtering / querying controls are added as children.
const ControlPanel = ({ children, width = PANEL_WIDTH, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const currLang = useSelector((state) => state.lang.value.currLang);
  const pageDir = useSelector((state) => state.lang.value.pageDir);
  const controlPanelOptions = useSelector((state) => state.lang.value.controlPanelOptions);
  const vizParamRadio = useSelector((state) => state.lang.value.realTimePanelTitles.vizParamRadio);
  const showVehicles = useSelector((state) => state.realtime.value.showVehicles);
  const vizParam = useSelector((state) => state.realtime.value.vizParam);
  const networkOpacity = useSelector((state) => state.realtime.value.networkOpacity);
  const dispatch = useDispatch();

  return (
    <Box
      sx={{
        position: "absolute",
        // Starts below the progress bar so it stays visible across the full width
        top: `${PROGRESS_BAR_HEIGHT}px`,
        right: 0,
        height: `calc(100% - ${PROGRESS_BAR_HEIGHT}px)`,
        zIndex: 30,
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        pt: `${PANEL_TOP_OFFSET}px`,
        pb: 2,
        pointerEvents: "none",
      }}
    >
      <Tooltip
        title={open ? controlPanelOptions.hidePanel[currLang] : controlPanelOptions.showPanel[currLang]}
        placement="left"
      >
        <IconButton
          aria-label={open ? "hide-control-panel" : "show-control-panel"}
          onClick={() => setOpen((prev) => !prev)}
          sx={{
            pointerEvents: "auto",
            // Sits alongside the panel heading rather than at the very top
            mt: "6px",
            p: 1,
            borderRadius: 2,
            color: "text.secondary",
            backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.92),
            backdropFilter: "blur(8px)",
            boxShadow: 3,
            "&:hover": {
              color: "text.primary",
              backgroundColor: "background.paper",
            },
          }}
        >
          {open ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Tooltip>

      <Paper
        elevation={0}
        dir={pageDir[currLang]}
        sx={{
          pointerEvents: open ? "auto" : "none",
          width: open ? `min(${width}px, 90vw)` : 0,
          maxHeight: "100%",
          overflow: "hidden",
          borderRadius: "16px 0 0 16px",
          backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.94),
          backdropFilter: "blur(10px)",
          boxShadow: open ? 8 : 0,
          transition: (theme) =>
            theme.transitions.create(["width", "box-shadow"], {
              duration: theme.transitions.duration.standard,
            }),
        }}
      >
        {/* Fixed width so the content does not reflow while the panel slides */}
        <Box
          sx={{
            width: `min(${width}px, 90vw)`,
            maxHeight: "100%",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
            <Typography
              variant="h6"
              component="h2"
              sx={{ fontSize: "1.05rem", letterSpacing: "0.04em" }}
            >
              {controlPanelOptions.title[currLang]}
            </Typography>
          </Box>
          <Divider />

          <Stack spacing={3.5} sx={{ px: 3, py: 3 }}>
            <Section label={controlPanelOptions.vehicles[currLang]}>
              <ToggleButton
                value="showVehicles"
                selected={showVehicles}
                onChange={() => dispatch(setShowVehicles(!showVehicles))}
                aria-label={showVehicles ? "hide-vehicles" : "show-vehicles"}
                fullWidth
                sx={{
                  justifyContent: "flex-start",
                  gap: 1.5,
                  px: 2,
                  py: 1.25,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "0.9rem",
                }}
              >
                <DirectionsBusIcon fontSize="small" />
                {showVehicles
                  ? controlPanelOptions.hideVehicles[currLang]
                  : controlPanelOptions.showVehicles[currLang]}
              </ToggleButton>
            </Section>

            <Section label={vizParamRadio.title[currLang]} labelId="viz-param-radio-label">
              <FormControl sx={{ display: "flex", m: 0 }}>
                <RadioGroup
                  aria-labelledby="viz-param-radio-label"
                  name="viz-param"
                  value={vizParam}
                  onChange={(event) => dispatch(setVizParam(event.target.value))}
                  sx={{ gap: 1 }}
                >
                  {VIZ_PARAMS.map(({ value, labelKey }) => (
                    <FormControlLabel
                      key={value}
                      value={value}
                      control={<Radio size="small" sx={{ p: 0.75 }} />}
                      label={vizParamRadio.options[labelKey][currLang]}
                      slotProps={{ typography: { fontSize: "0.9rem" } }}
                      sx={{
                        m: 0,
                        px: 1.25,
                        py: 0.75,
                        gap: 1,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: vizParam === value ? "primary.main" : "divider",
                        backgroundColor: (theme) =>
                          vizParam === value
                            ? alpha(theme.palette.primary.main, 0.08)
                            : "transparent",
                        transition: (theme) =>
                          theme.transitions.create(["background-color", "border-color"], {
                            duration: theme.transitions.duration.shortest,
                          }),
                        "&:hover": { backgroundColor: "action.hover" },
                      }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Section>

            <Section
              label={controlPanelOptions.networkOpacity[currLang]}
              labelId="network-opacity-label"
              readout={`${Math.round(networkOpacity * 100)}%`}
            >
              {/* Inset so the thumb is not clipped at either end of the track */}
              <Box sx={{ px: 1 }}>
                <Slider
                  aria-labelledby="network-opacity-label"
                  value={networkOpacity}
                  onChange={(event, value) => dispatch(setNetworkOpacity(value))}
                  min={0}
                  max={1}
                  step={0.05}
                  size="small"
                />
              </Box>
            </Section>
            {children}
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default ControlPanel;
